import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { getVanillaEl } from "../../domBridge";
import { apiFetch } from "../../../api";
import { requestLock } from "../../../lockBus";
import { confirmDialog } from "../../../dialogs";
import { showToast } from "../../../toast";
import { formatRelativeTime } from "../../../utils";

interface SessionRow {
  sessionId: string;
  device: string;
  createdAt: string | null;
  lastSeenAt: string | null;
  isCurrent: boolean;
}

// /profile/sessions - split out of the main Profile page (progressive
// disclosure) so a security-heavy list doesn't make the main page feel
// long. Same GET /api/sessions + POST /api/sessions/:id/revoke as the old
// ProfileDialog, unchanged - only the presentation (grouped "this device"
// vs "other sessions", a confirm step before revoking) is new.
export function SessionsScreen() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SessionRow[] | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  async function loadSessions(): Promise<void> {
    try {
      const res = await apiFetch("/api/sessions");
      if (res.status === 401) {
        requestLock("unauthorized");
        return;
      }
      if (!res.ok) return;
      const data = await res.json();
      setSessions(Array.isArray(data.sessions) ? data.sessions : []);
    } catch {
      // Best effort - the page just stays on its loading state.
    }
  }

  useEffect(() => {
    loadSessions();
  }, []);

  async function handleRevoke(session: SessionRow): Promise<void> {
    const ok = await confirmDialog({
      title: "Log out this device?",
      message: `${session.device} will need to sign in again.`,
      confirmLabel: "Log out",
      danger: true,
    });
    if (!ok) return;

    setRevokingId(session.sessionId);
    try {
      const res = await apiFetch(`/api/sessions/${session.sessionId}/revoke`, { method: "POST" });
      if (res.status === 401) {
        requestLock("unauthorized");
        return;
      }
      if (!res.ok) {
        showToast("Failed to log out that device.", "error");
        return;
      }
      setSessions((prev) => (prev ? prev.filter((s) => s.sessionId !== session.sessionId) : prev));
      showToast("Device logged out.", "success");
    } catch {
      showToast("Server error. Try again.", "error");
    } finally {
      setRevokingId(null);
    }
  }

  const container = getVanillaEl<HTMLDivElement>("profileSessionsView");
  if (!container) return null;

  const current = sessions?.find((s) => s.isCurrent) ?? null;
  const others = sessions?.filter((s) => !s.isCurrent) ?? [];

  return createPortal(
    <>
      <div className="profile-page-header">
        <button type="button" className="btn btn-outline-pink btn-sm" aria-label="Back" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left" aria-hidden="true" />
        </button>
        <span className="profile-page-header-title">Active sessions</span>
      </div>

      <div className="profile-page-content">
        <p className="small muted mb-0">Devices where your account is currently signed in.</p>

        {sessions === null ? (
          <p className="small muted text-center mt-4">Loading…</p>
        ) : (
          <>
            {current && (
              <>
                <div className="session-group-label">This device</div>
                <div className="session-card">
                  <span className="session-card-icon">
                    <i className="bi bi-phone" aria-hidden="true" />
                  </span>
                  <div className="session-card-body">
                    <div className="session-card-device">{current.device}</div>
                    <div className="session-card-meta">
                      <span className="session-current-dot" aria-hidden="true" />
                      Active now
                    </div>
                  </div>
                </div>
              </>
            )}

            {!!others.length && (
              <>
                <div className="session-group-label">Other sessions</div>
                {others.map((s) => (
                  <div key={s.sessionId} className="session-card">
                    <span className="session-card-icon">
                      <i className="bi bi-phone" aria-hidden="true" />
                    </span>
                    <div className="session-card-body">
                      <div className="session-card-device">{s.device}</div>
                      <div className="session-card-meta">{s.lastSeenAt ? `Active ${formatRelativeTime(s.lastSeenAt)} ago` : "Unknown"}</div>
                    </div>
                    <div className="profile-row-action">
                      <button
                        type="button"
                        className="btn btn-outline-pink btn-sm"
                        onClick={() => handleRevoke(s)}
                        disabled={revokingId === s.sessionId}
                      >
                        {revokingId === s.sessionId ? "…" : "Log out"}
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {!current && !others.length && <p className="small muted text-center mt-4">No active sessions found.</p>}
          </>
        )}
      </div>
    </>,
    container,
  );
}
